// ---------------------------------------------------------------------------
// card-grid-chrome — the shared card wrapper (header + handle + controls + body)
// ---------------------------------------------------------------------------
//
// Single owner of card chrome and its accessibility affordances. Drag is never
// the only way to reorder OR resize: keyboard Move up/down (boundary-disabled)
// and −/+ width/height buttons are first-class. The drag HANDLE is the reorder
// drag origin; the card ROOT is the drop target. Edge resize handles are pointer
// affordances (aria-hidden) whose keyboard equivalent is the size buttons. All
// icon-only buttons carry aria-labels.
//
// Per-card `capabilities` gate which affordances render: a disabled affordance
// is OMITTED (no drag handle when not reorderable; no ±width/col-handle when
// columns can't resize; no ±height/row-handle when rows can't; no hide button
// when not hideable). `undefined` capabilities ≡ all enabled.

import { createElement } from '../../core/dom/helpers.js';
import { REORDER_KEY_ATTR } from '../../core/dnd/reorderable-list.js';

const COL_MIN = 1;
const COL_MAX_ABS = 4;
const ROW_MIN = 1;
const ROW_MAX_ABS = 6; // absolute ceiling; per-card maxRowSpan caps below via setResizeBounds

/**
 * @typedef {import('../../contracts/card-grid.js').CardCapabilities} CardCapabilities
 * @typedef {import('./card-grid-controller.js').CardGridLabels} CardGridLabels
 */

/**
 * @param {{
 *   id: string,
 *   title: string,
 *   kind?: string,
 *   capabilities?: CardCapabilities,
 *   labels: Required<CardGridLabels>,
 *   onHide: () => void,
 *   onMoveUp: () => void,
 *   onMoveDown: () => void,
 *   canMoveUp: boolean,
 *   canMoveDown: boolean,
 *   colSpan?: 1|2|3|4,
 *   rowSpan?: import('../../contracts/card-grid.js').CardGridRowSpan,
 *   colMax?: number,
 *   rowMax?: number,
 *   onResize?: (axis: 'col'|'row', delta: -1|1) => void,
 * }} opts
 * @returns {{
 *   el: HTMLElement, handle: HTMLElement | null, body: HTMLElement,
 *   setSpan: (size: {colSpan: number, rowSpan: number}) => void,
 *   setResizeBounds: (colMax: number, rowMax: number) => void,
 *   destroy: () => void,
 * }}
 */
export function createCardGridChrome(opts) {
  const { id, title, labels } = opts;
  const cap = opts.capabilities || {};
  const reorderable = cap.reorderable !== false;
  const hideable = cap.hideable !== false;
  const colResizable = cap.resize?.columns !== false;
  const rowResizable = cap.resize?.rows !== false;

  // Current RAW span (may exceed the breakpoint max; SCSS clamps the visual).
  let colSpan = clamp(opts.colSpan || 1, COL_MIN, COL_MAX_ABS);
  let rowSpan = clamp(opts.rowSpan || 1, ROW_MIN, ROW_MAX_ABS);
  // Current per-breakpoint maxima (drive the "+" button disabled state).
  let colMax = opts.colMax ?? COL_MAX_ABS;
  let rowMax = opts.rowMax ?? ROW_MAX_ABS;

  const titleId = `card-grid-card-title-${id}`;

  // Only the handle is draggable (reorder drag origin); the card root is the
  // drop target. Omitted entirely when the card is not reorderable.
  const handle = reorderable
    ? createElement('span', {
      class: 'card-grid__card-handle',
      'aria-hidden': 'true',
      title: labels.dragToReorder,
      draggable: 'true',
    }, ['☰'])
    : null;

  const heading = createElement('h2', { class: 'card-grid__card-title', id: titleId }, [title]);

  /** @type {HTMLButtonElement | null} */
  const moveUpBtn = reorderable ? btn('card-grid__card-btn--move-up', labels.moveUp(title), '↑') : null;
  /** @type {HTMLButtonElement | null} */
  const moveDownBtn = reorderable ? btn('card-grid__card-btn--move-down', labels.moveDown(title), '↓') : null;
  /** @type {HTMLButtonElement | null} */
  const widthDownBtn = colResizable ? btn('card-grid__card-btn--width-down', labels.widthDown(title), '⇤') : null;
  /** @type {HTMLButtonElement | null} */
  const widthUpBtn = colResizable ? btn('card-grid__card-btn--width-up', labels.widthUp(title), '⇥') : null;
  /** @type {HTMLButtonElement | null} */
  const heightDownBtn = rowResizable ? btn('card-grid__card-btn--height-down', labels.heightDown(title), '⤒') : null;
  /** @type {HTMLButtonElement | null} */
  const heightUpBtn = rowResizable ? btn('card-grid__card-btn--height-up', labels.heightUp(title), '⤓') : null;
  /** @type {HTMLButtonElement | null} */
  const hideBtn = hideable ? btn('card-grid__card-btn--hide', labels.hide(title), '✕') : null;

  const controlEls = /** @type {HTMLElement[]} */ (
    [moveUpBtn, moveDownBtn, widthDownBtn, widthUpBtn, heightDownBtn, heightUpBtn, hideBtn].filter(Boolean)
  );
  const controls = createElement('div', { class: 'card-grid__card-controls' }, controlEls);

  const headerChildren = /** @type {(string|Node)[]} */ ([handle, heading, controls].filter(Boolean));
  const header = createElement('div', { class: 'card-grid__card-header' }, headerChildren);

  const body = createElement('div', { class: 'card-grid__card-body' });

  // Pointer resize affordances (keyboard path is the size buttons above) — each
  // omitted when its axis can't resize.
  const resizeCol = colResizable
    ? createElement('span', { class: 'card-grid__card-resize card-grid__card-resize--col', 'aria-hidden': 'true' })
    : null;
  const resizeRow = rowResizable
    ? createElement('span', { class: 'card-grid__card-resize card-grid__card-resize--row', 'aria-hidden': 'true' })
    : null;

  const elChildren = /** @type {(string|Node)[]} */ ([header, body, resizeCol, resizeRow].filter(Boolean));
  const el = createElement('article', {
    class: 'card-grid__card',
    'aria-labelledby': titleId,
  }, elChildren);
  el.setAttribute(REORDER_KEY_ATTR, id);
  // `kind` is a CSS hook (e.g. data-card-kind="table"). Sanitize to a safe token
  // (lowercase, [a-z0-9-]) and only stamp when non-empty — it's contractually an
  // arbitrary string.
  const kindToken = String(opts.kind || '').toLowerCase().replace(/[^a-z0-9-]/g, '');
  if (kindToken) el.setAttribute('data-card-kind', kindToken);

  function writeSpanAttrs() {
    el.setAttribute('data-col-span', String(colSpan));
    el.setAttribute('data-row-span', String(rowSpan));
  }

  function refreshSizeDisabled() {
    // "+" disabled at/over the breakpoint max; "−" disabled at absolute min.
    if (widthUpBtn) widthUpBtn.disabled = colSpan >= colMax;
    if (widthDownBtn) widthDownBtn.disabled = colSpan <= COL_MIN;
    if (heightUpBtn) heightUpBtn.disabled = rowSpan >= rowMax;
    if (heightDownBtn) heightDownBtn.disabled = rowSpan <= ROW_MIN;
  }

  writeSpanAttrs();
  refreshSizeDisabled();

  // Boundary-disabled keyboard reorder buttons.
  if (moveUpBtn) moveUpBtn.disabled = !opts.canMoveUp;
  if (moveDownBtn) moveDownBtn.disabled = !opts.canMoveDown;

  /** @type {Array<() => void>} */
  const cleanups = [];
  /** @param {HTMLButtonElement | null} button @param {() => void} fn */
  function wire(button, fn) {
    if (!button) return;
    button.addEventListener('click', fn);
    cleanups.push(() => button.removeEventListener('click', fn));
  }
  wire(moveUpBtn, () => opts.onMoveUp());
  wire(moveDownBtn, () => opts.onMoveDown());
  wire(hideBtn, () => opts.onHide());
  wire(widthDownBtn, () => opts.onResize?.('col', -1));
  wire(widthUpBtn, () => opts.onResize?.('col', 1));
  wire(heightDownBtn, () => opts.onResize?.('row', -1));
  wire(heightUpBtn, () => opts.onResize?.('row', 1));

  return {
    el,
    handle,
    body,
    /** Update the RAW span (in place, no remount) + refresh size-button state. */
    setSpan(size) {
      colSpan = clamp(size.colSpan, COL_MIN, COL_MAX_ABS);
      rowSpan = clamp(size.rowSpan, ROW_MIN, ROW_MAX_ABS);
      writeSpanAttrs();
      refreshSizeDisabled();
    },
    /** Update the per-breakpoint maxima + refresh size-button disabled state. */
    setResizeBounds(nextColMax, nextRowMax) {
      colMax = nextColMax;
      rowMax = nextRowMax;
      refreshSizeDisabled();
    },
    destroy() {
      for (const c of cleanups) c();
    },
  };
}

/** @param {string} cls @param {string} label @param {string} glyph @returns {HTMLButtonElement} */
function btn(cls, label, glyph) {
  return /** @type {HTMLButtonElement} */ (createElement('button', {
    type: 'button',
    class: `card-grid__card-btn ${cls}`,
    'aria-label': label,
  }, [glyph]));
}

/** @param {number} v @param {number} min @param {number} max */
function clamp(v, min, max) {
  return Math.min(max, Math.max(min, Math.round(Number(v) || min)));
}
